import React, { useState, useCallback } from 'react';
import {
  Spacing,
  Typography,
  Button,
  Dialog,
  TitleText,
  Divider,
  StyledDiv,
  QuestionTypeList,
  Badge,
  ExpandableCard,
  Card,
  PercentageLevel,
} from '@taleemabad/dsm/components';
import {
  analyticsService,
  EventNames,
  IBookChapter,
  IQuestion,
  ISchoolClassInfo,
} from '@taleemabad/db';
import { SelectedOptions } from '@taleemabad/providers';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { NavigateFunction } from 'react-router-dom';
import { transformQuestionTag } from '@taleemabad/dsm/utils';

export interface ExamQuestionTypesTemplateProps {
  selectedOptions?: SelectedOptions;
  selectedQuestions?: IQuestion[];
  onQuestionTypeClick: (questionType: string) => void;
  onDialogNext: () => void;
  comingSoonDialog: boolean;
  setComingSoonDialog: (value: boolean) => void;
  navigate: NavigateFunction;
  questionTypesInDesiredOrder: string[];
  joinedChapterNumbers: (chapters: IBookChapter[] | null) => string;
  selectedExamType: string;
  totalMarks: number;
  classDetails: ISchoolClassInfo | null;
  taxonomyPercentages: PercentageLevel[];
  questionCategoryPercentages: PercentageLevel[];
  handleGenerateExamClick: () => void;
  handleEditChaptersClick: () => void;
}

export const ExamQuestionTypesTemplate: React.FC<
  ExamQuestionTypesTemplateProps
> = ({
  selectedOptions,
  selectedQuestions = [],
  onQuestionTypeClick,
  onDialogNext,
  comingSoonDialog,
  setComingSoonDialog,
  navigate,
  questionTypesInDesiredOrder,
  joinedChapterNumbers,
  selectedExamType,
  totalMarks,
  classDetails,
  taxonomyPercentages,
  questionCategoryPercentages,
  handleGenerateExamClick,
  handleEditChaptersClick,
}) => {
  const [showInstructions, setShowInstructions] = useState<boolean>(false);

  const handleDialogNext = useCallback(() => {
    setShowInstructions(false);
    onDialogNext();
  }, [onDialogNext]);

  return (
    <>
      <StyledContent>
        <Spacing>
          <SelectedOptionsWrapper>
            <TitleTextContainer>
              <TitleText
                leftText={classDetails?.gradeLabel || ''}
                seperatorIconName="circle"
                rightText={classDetails?.subjectLabel || ''}
              />
              <Spacing marginTop="md">
                <Typography variant="textLabelMediumSurfaceVariant">
                  Chapter{' '}
                  {selectedOptions
                    ? joinedChapterNumbers(selectedOptions?.selectedChapters)
                    : ''}
                </Typography>
              </Spacing>
            </TitleTextContainer>
            <Spacing marginTop="xl" marginBottom="xl">
              <Divider />
            </Spacing>
            <StyledCard variant="filled">
              <StyledDiv justifyContent="space-between">
                <Typography variant="textLabelLargePrimary">
                  {selectedExamType}
                </Typography>
                <StyledDiv gap={6}>
                  <Typography variant="textLabelLargeOnSurface">
                    Total Marks
                  </Typography>
                  <Typography variant="textLabelLargePrimary">
                    {totalMarks}
                  </Typography>
                </StyledDiv>
              </StyledDiv>
            </StyledCard>
          </SelectedOptionsWrapper>
          <ExpandableCard
            title="Selected Questions Percentage"
            defaultExpanded={true}
          >
            <Spacing marginTop="sm">
              <StyledDiv gap={4} justifyContent="space-between" flexWrap="wrap">
                {questionCategoryPercentages.map((item: PercentageLevel) => (
                  <StyledBadgeItem key={item.name}>
                    <Badge
                      size="large"
                      label={transformQuestionTag(item.name)}
                      color={item.color}
                    />
                    <StyledBadgePercentage variant="textTitleSmallonSurface">
                      {item.percentage}%
                    </StyledBadgePercentage>
                  </StyledBadgeItem>
                ))}
              </StyledDiv>
            </Spacing>
          </ExpandableCard>
          <ExpandableCard
            title="Bloom's Taxonomy Levels"
            defaultExpanded={true}
          >
            <Spacing marginTop="sm">
              <StyledDiv gap={4} justifyContent="space-between" flexWrap="wrap">
                {taxonomyPercentages.map((item: PercentageLevel) => (
                  <StyledBadgeItem key={item.name}>
                    <Badge size="small" label={item.name} color={item.color} />
                    <StyledBadgePercentage variant="textTitleSmallonSurface">
                      {item.percentage}%
                    </StyledBadgePercentage>
                  </StyledBadgeItem>
                ))}
              </StyledDiv>
            </Spacing>
          </ExpandableCard>
          <Spacing marginBottom="xl">
            <Typography
              variant="textBodyMedium"
              data-cy="exam-generator-select-question-type-title"
            >
              Select the question type
            </Typography>
          </Spacing>

          <QuestionTypeList
            questionTypes={questionTypesInDesiredOrder}
            onQuestionTypeClick={onQuestionTypeClick}
            selectedQuestions={selectedQuestions}
          />

          <ActionButtonContainer>
            <Button
              children="Edit Chapters"
              variant="outlined"
              onClick={handleEditChaptersClick}
            />
            <Button
              data-cy="exam-generator-generate-exam-button"
              children="Generate Exam"
              disabled={selectedQuestions.length === 0}
              onClick={() => {
                handleGenerateExamClick();
                setShowInstructions(true);
              }}
            />
          </ActionButtonContainer>
        </Spacing>
      </StyledContent>

      <Dialog
        open={showInstructions}
        hasBackdropClick={false}
        title="Assessment Structure"
        onClose={() => setShowInstructions(false)}
      >
        <Typography variant="textTitleSmallonSurface">
          Your selected questions breakdown
        </Typography>
        <Spacing marginTop="sm">
          <Spacing marginBottom="lg">
            <Typography variant="textBodyMediumSurface">
              Selected Question Percentage
            </Typography>
          </Spacing>
          <StyledDiv gap={4} justifyContent="space-between" flexWrap="wrap">
            {questionCategoryPercentages.map((item: PercentageLevel) => (
              <StyledBadgeItem key={item.name}>
                <Badge
                  size="large"
                  label={transformQuestionTag(item.name)}
                  color={item.color}
                />
                <StyledBadgePercentage variant="textTitleSmallonSurface">
                  {item.percentage}%
                </StyledBadgePercentage>
              </StyledBadgeItem>
            ))}
          </StyledDiv>
        </Spacing>

        <Spacing marginTop="sm">
          <Spacing marginBottom="lg">
            <Typography variant="textBodyMediumSurface">
              Percentage of Selected Bloom's Taxonomy Questions
            </Typography>
          </Spacing>
          <StyledDiv gap={7} justifyContent="space-between" flexWrap="wrap">
            {taxonomyPercentages.map((item: PercentageLevel) => (
              <SingleColumnBadgeItem key={item.name}>
                <Badge size="small" label={item.name} color={item.color} />
                <StyledBadgePercentage variant="textTitleSmallonSurface">
                  {item.percentage}%
                </StyledBadgePercentage>
              </SingleColumnBadgeItem>
            ))}
          </StyledDiv>
        </Spacing>
        <StyledDiv justifyContent="center" gap={8}>
          <Button
            variant="outlined"
            children="Edit Exam"
            width="50%"
            onClick={() => {
              setShowInstructions(false);
            }}
          />
          <Button
            variant="filled"
            data-cy="exam-generator-assessment-structure-next-button"
            children="Next"
            width="50%"
            onClick={handleDialogNext}
          />
        </StyledDiv>
      </Dialog>
      <Dialog
        open={comingSoonDialog}
        hasBackdropClick={false}
        title="Coming Soon"
        onClose={() => setComingSoonDialog(false)}
      >
        <Spacing marginTop="sm" marginBottom="lg">
          <Typography variant="textBodyMedium">
            Exam of this grade & subject is under development. Stay tuned.
          </Typography>
        </Spacing>
        <StyledDiv
          justifyContent="center"
          gap={8}
          margin="var(--rk-spacing-xl)"
        >
          <Button
            variant="filled"
            children="Got It"
            width="100%"
            onClick={() => {
              setComingSoonDialog(false);
              navigate(-1);
            }}
          />
        </StyledDiv>
      </Dialog>
    </>
  );
};

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  ${mq(BreakpointSizes.xs)} {
    gap: var(--rk-spacing-md);
  }
`;

const TitleTextContainer = styled.div`
  margin: var(--rk-spacing-xxl) 0;
  ${mq(BreakpointSizes.xs)} {
    margin: var(--rk-spacing-xl) 0;
  }
`;

const SelectedOptionsWrapper = styled.div`
  padding: var(--rk-spacing-lg) 0;
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--md-sys-color-on-primary);
`;

const StyledBadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-sm);
  width: calc(50% - var(--rk-spacing-lg));
`;

const SingleColumnBadgeItem = styled(StyledBadgeItem)`
  width: 50%;
`;

const StyledBadgePercentage = styled(Typography)`
  text-align: right;
  flex-grow: 1;
`;

const StyledCard = styled(Card)`
  border-radius: 10px;
  min-height: auto;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-xl);
  justify-content: left;
  margin-right: var(--rk-spacing-xl);
  width: -webkit-fill-available;
  padding: var(--rk-spacing-xxl) 0;
  position: fixed;
  background-color: var(--md-ref-neutral-100);
  bottom: 0;

  ${mq(BreakpointSizes.xs)} {
    justify-content: center;
    md-filled-button,
    md-outlined-button {
      flex: 1;
    }
  }
`;
